import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../../lib/api.js'
import {
  Card, CopyButton, CopyRow, Field, Page, StepNav, SubmitButton, useAsync, useConfirm, useToast
} from '../../components/Ui.jsx'
import { publicHatcheryUrl, whatsappUrl } from '../../lib/share.js'

const BLANK = {
  code: '', name: '', ownerName: '', phone: '', email: '', caaRegistrationNo: '',
  address: '', village: '', mandal: '', district: 'Kakinada',
  lat: '', lng: '', annualCapacityCr: '', broodstockPartners: '', biosecurityNotes: '',
  username: '', password: ''
}

// Common landing points in the belt, so nobody has to hunt for coordinates
// while a hatchery owner waits on the phone.
const PRESETS = [
  { label: 'Kakinada town', lat: 16.9891, lng: 82.2475 },
  { label: 'Vakalapudi', lat: 17.0180, lng: 82.2560 },
  { label: 'Uppada', lat: 17.0800, lng: 82.3300 },
  { label: 'U. Kothapalli', lat: 17.1250, lng: 82.3450 },
  { label: 'Mulapeta', lat: 17.2400, lng: 82.4700 },
  { label: 'Tuni', lat: 17.3500, lng: 82.5500 }
]

const STEPS = [
  { key: 'who', title: 'Hatchery', hint: 'Name & registration' },
  { key: 'where', title: 'Location', hint: 'Drives haul distance' },
  { key: 'what', title: 'Capability', hint: 'Capacity & biosecurity' },
  { key: 'login', title: 'Portal login', hint: 'Issued to the owner' },
  { key: 'review', title: 'Review', hint: 'Check, then create' }
]

/** Guided onboarding: one decision per screen, nothing saved until the last step. */
export default function HatcheryOnboard () {
  const navigate = useNavigate()
  const toast = useToast()
  const confirm = useConfirm()

  const [form, setForm] = useState(BLANK)
  const [step, setStep] = useState(0)
  const [furthest, setFurthest] = useState(0)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(null) // { hatchery, credentials }

  const nextCode = useAsync(() => api.get('/admin/hatcheries/next-code'))

  useEffect(() => {
    if (nextCode.data?.code) setForm(f => (f.code ? f : { ...f, code: nextCode.data.code }))
  }, [nextCode.data])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const problem = i => {
    if (i === 0) {
      if (!form.name.trim()) return 'The hatchery needs a name.'
      if (!form.code.trim()) return 'A hatchery code is required — it prefixes every batch code.'
    }
    if (i === 1 && (form.lat === '' || form.lng === '')) {
      return 'Latitude and longitude are required — matching ranks hatcheries by road distance to the pond.'
    }
    return null
  }

  const go = i => {
    if (i > step) {
      for (let s = step; s < i; s++) {
        const err = problem(s)
        if (err) { setStep(s); return toast.error(err) }
      }
    }
    setStep(i)
    setFurthest(f => Math.max(f, i))
  }

  const submit = async e => {
    e.preventDefault()
    for (let s = 0; s < STEPS.length; s++) {
      const err = problem(s)
      if (err) { setStep(s); return toast.error(err) }
    }
    const ok = await confirm({
      title: 'Create this hatchery and issue its login?',
      body: 'The hatchery record is created and a portal login is issued in the same step. It can sign in and post batches immediately.',
      facts: [
        ['Hatchery', form.name],
        ['Code', form.code],
        ['Place', [form.village, form.mandal].filter(Boolean).join(', ') || 'not stated'],
        ['Username', form.username || 'derived from the name']
      ],
      confirmLabel: 'Create & issue login'
    })
    if (!ok) return
    setBusy(true)
    try {
      const res = await api.post('/admin/hatcheries', form)
      setDone(res)
      toast.ok(`${res.hatchery.name} is on the platform.`)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (done) return <Issued done={done} onAnother={() => { setDone(null); setForm({ ...BLANK, code: '' }); setStep(0); setFurthest(0); nextCode.reload() }} navigate={navigate} />

  const last = step === STEPS.length - 1

  return (
    <Page
      crumb={<Link to="/admin/hatcheries">← Hatcheries</Link>}
      title="Onboard a hatchery"
      subtitle="Five short steps. Nothing is saved until you press create on the last one — the login is issued at the same moment so you can read it out while the owner is still on the phone."
    >
      <div className="wizard">
        <StepNav steps={STEPS} current={step} furthest={furthest} onGo={go} />

        <form onSubmit={submit} className="wizard-body">
          {step === 0 && (
            <Card title="Who is the hatchery">
              <div className="formgrid">
                <Field label="Hatchery name" required>
                  <input value={form.name} onChange={e => set('name', e.target.value)} autoFocus
                    placeholder="e.g. Coastal Crest Aqua" />
                </Field>
                <Field label="Hatchery code" required hint="Auto-allocated. Change it only if the hatchery already has its own code.">
                  <input value={form.code} onChange={e => set('code', e.target.value)} />
                </Field>
                <Field label="Owner / contact person">
                  <input value={form.ownerName} onChange={e => set('ownerName', e.target.value)} />
                </Field>
                <Field label="Phone" hint="Desk use only — farmers never see this number.">
                  <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 …" />
                </Field>
                <Field label="Email">
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                </Field>
                <Field label="CAA registration number" hint="Coastal Aquaculture Authority number, shown on every batch.">
                  <input value={form.caaRegistrationNo} onChange={e => set('caaRegistrationNo', e.target.value)}
                    placeholder="AP/CAA/2026/…" />
                </Field>
              </div>
            </Card>
          )}

          {step === 1 && (
            <Card title="Where it sits">
              <p className="muted small" style={{ marginTop: 0 }}>
                Matching ranks hatcheries by road distance from the farmer's pond, and the liaison officer's
                trip is planned off the same point. Use the quick-fill for a village on the belt, then nudge
                the numbers if you have exact coordinates.
              </p>
              <div className="chips">
                <span className="muted small" style={{ alignSelf: 'center', marginRight: 4 }}>Quick fill:</span>
                {PRESETS.map(p => (
                  <button type="button" key={p.label} className="chip"
                    onClick={() => setForm(f => ({ ...f, lat: p.lat, lng: p.lng, village: f.village || p.label, mandal: f.mandal || p.label }))}>
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="formgrid">
                <Field label="Village"><input value={form.village} onChange={e => set('village', e.target.value)} /></Field>
                <Field label="Mandal"><input value={form.mandal} onChange={e => set('mandal', e.target.value)} /></Field>
                <Field label="District"><input value={form.district} onChange={e => set('district', e.target.value)} /></Field>
                <Field label="Latitude" required>
                  <input type="number" step="0.0001" value={form.lat} onChange={e => set('lat', e.target.value)} />
                </Field>
                <Field label="Longitude" required>
                  <input type="number" step="0.0001" value={form.lng} onChange={e => set('lng', e.target.value)} />
                </Field>
                <Field label="Address" wide><input value={form.address} onChange={e => set('address', e.target.value)} /></Field>
              </div>
            </Card>
          )}

          {step === 2 && (
            <Card title="What it can do">
              <p className="muted small" style={{ marginTop: 0 }}>
                This is what the operator reads out when pitching the hatchery, and what a farmer sees on the
                public page. All of it can be edited later by the hatchery itself.
              </p>
              <div className="formgrid">
                <Field label="Annual capacity (crore PL)">
                  <input type="number" step="0.1" value={form.annualCapacityCr} onChange={e => set('annualCapacityCr', e.target.value)} />
                </Field>
                <Field label="Broodstock partners" hint="Kona Bay, SIS, Benchmark, local multiplication centre…">
                  <input value={form.broodstockPartners} onChange={e => set('broodstockPartners', e.target.value)} />
                </Field>
                <Field label="Biosecurity notes" wide>
                  <textarea rows={4} value={form.biosecurityNotes} onChange={e => set('biosecurityNotes', e.target.value)}
                    placeholder="Filtration, effluent handling, screening routine — what the operator can tell a farmer." />
                </Field>
              </div>
            </Card>
          )}

          {step === 3 && (
            <Card title="The login you hand over">
              <p className="muted small" style={{ marginTop: 0 }}>
                Leave both blank and the platform generates them — the password is deliberately readable out
                loud, e.g. <span className="mono">Tide@4417</span>. Either way you see the credentials on the
                next screen, and they stay readable on the hatchery page until the owner changes the password.
              </p>
              <div className="formgrid">
                <Field label="Username" hint="Blank = derived from the hatchery name.">
                  <input value={form.username} onChange={e => set('username', e.target.value)} autoComplete="off" />
                </Field>
                <Field label="Password" hint="Blank = a readable one is generated.">
                  <input value={form.password} onChange={e => set('password', e.target.value)} autoComplete="off" />
                </Field>
              </div>
              <div className="alert info">
                <div>
                  <b>There is no self-service password reset</b>
                  If the hatchery loses it, you reissue from Logins &amp; staff and read out the new one.
                </div>
              </div>
            </Card>
          )}

          {step === 4 && (
            <Card title="Check before creating">
              <div className="reviewgrid">
                <Section title="Hatchery" onEdit={() => setStep(0)} rows={[
                  ['Name', form.name], ['Code', form.code], ['Owner', form.ownerName],
                  ['Phone', form.phone], ['Email', form.email], ['CAA reg.', form.caaRegistrationNo]
                ]} />
                <Section title="Location" onEdit={() => setStep(1)} rows={[
                  ['Village', form.village], ['Mandal', form.mandal], ['District', form.district],
                  ['Coordinates', form.lat && form.lng ? `${form.lat}, ${form.lng}` : ''],
                  ['Address', form.address]
                ]} />
                <Section title="Capability" onEdit={() => setStep(2)} rows={[
                  ['Annual capacity', form.annualCapacityCr ? `${form.annualCapacityCr} crore PL` : ''],
                  ['Broodstock', form.broodstockPartners],
                  ['Biosecurity', form.biosecurityNotes]
                ]} />
                <Section title="Login" onEdit={() => setStep(3)} rows={[
                  ['Username', form.username || 'generated from the name'],
                  ['Password', form.password || 'generated, readable out loud']
                ]} />
              </div>
            </Card>
          )}

          <div className="wizard-foot">
            <button type="button" className="btn" disabled={step === 0} onClick={() => go(step - 1)}>Back</button>
            <div className="spacer" />
            <span className="muted small">Step {step + 1} of {STEPS.length}</span>
            {last
              ? <SubmitButton busy={busy} type="submit">Create hatchery &amp; issue login</SubmitButton>
              : <button type="button" className="btn primary" onClick={() => go(step + 1)}>Continue</button>}
          </div>
        </form>
      </div>
    </Page>
  )
}

function Section ({ title, rows, onEdit }) {
  return (
    <div className="reviewsec">
      <div className="reviewsec-head">
        <h3>{title}</h3>
        <button type="button" className="btn sm ghost" onClick={onEdit}>Edit</button>
      </div>
      <dl>
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd className={v ? '' : 'muted'}>{v || '—'}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

/** Success state: hand over the credentials, then say what happens next. */
function Issued ({ done, onAnother, navigate }) {
  const { hatchery, credentials } = done
  const publicUrl = publicHatcheryUrl(hatchery._id)
  const handover = `Aqua Platform login for ${hatchery.name}
Sign in: ${window.location.origin}/login
Username: ${credentials.username}
Password: ${credentials.password}

Post your ready batches here and bookings from the desk will appear on the same screen.`

  return (
    <Page
      title={`${hatchery.name} is on the platform`}
      subtitle="Read the login out to the owner now — the password stays visible on the hatchery page until they change it themselves."
    >
      <div className="split">
        <Card title="Credentials to hand over" className="issued">
          <CopyRow label="Username" value={credentials.username} />
          <CopyRow label="Password" value={credentials.password} hint="Readable out loud on a bad phone line." />
          <CopyRow label="Sign-in address" value={`${window.location.origin}/login`} wrap />
          <div className="btnrow">
            <CopyButton className="btn primary" value={handover} told="Handover message copied">Copy the whole message</CopyButton>
            <a className="btn" href={whatsappUrl(handover, hatchery.phone)} target="_blank" rel="noreferrer">
              Send on WhatsApp
            </a>
          </div>
        </Card>

        <Card title="What happens next">
          <ol className="flowsteps">
            <li><b>The hatchery signs in and posts a batch</b><span>Stage, ready date, capacity, PCR report and tank photos.</span></li>
            <li><b>The batch appears in matching</b><span>The desk can book it for a farmer the moment it is live.</span></li>
            <li><b>Farmers see the hatchery on the public link</b><span>Without prices or phone numbers, unless you turn prices on in Settings.</span></li>
          </ol>
          <CopyRow label="This hatchery's public page" value={publicUrl} wrap />
        </Card>
      </div>

      <div className="btnrow">
        <button className="btn primary" onClick={() => navigate(`/admin/hatcheries/${hatchery._id}`)}>Open hatchery</button>
        <button className="btn" onClick={onAnother}>Onboard another</button>
        <Link className="btn" to="/admin/hatcheries">Back to hatcheries</Link>
      </div>
    </Page>
  )
}
